import React from 'react';
import { connect } from 'react-redux';
import { message } from 'antd';
import { WorkOrderLabel } from '../../data/workOrderStarLabels';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import {
	workOrderLabelsRestCrudThunks,
	updateWorkOrderLabels,
} from '../../thunks/work_order_labels_thunks';
import { setWorkOrderLabelsPendingInStore } from '../../actions/work_orders_actions';
import { pickRandomLabelColor } from '../../constants/labelConstants';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { isUserAllowedToAccess, PERMISSION_NAMES } from '../../utils/AuthUtils';
import { useNotesBroadcast } from '../../hooks/useNotesBroadcast';
import { WEB_SOCKET_ENTITY_TYPES } from '../../utils/WebSocketUtils';
import { AddLabelModal } from './AddLabelModal';

// Private to this dropdown's server-side search so it never clobbers the shared
// catalogue the Work Orders "Labels" advanced filter reads.
const DROPDOWN_TARGET_COLLECTION = 'workOrderLabelsDropdown';
// Shared catalogue — create() writes here so the advanced filter picks up new labels.
const LABELS_TARGET_COLLECTION = 'workOrderLabelsIndex';
const LABELS_SORT = { sort_by: 'label', order: 'asc' };

interface WorkOrderOwnProps {
	visible: boolean;
	onClose: () => void;
	workOrderId: number | string;
	existingLabels?: WorkOrderLabel[];
	// Re-runs the list query so a row drops out when a label filter no longer matches it.
	refreshTable?: () => void;
}

interface WorkOrderProps extends WorkOrderOwnProps {
	canModifyLabels: boolean;
	canApplyLabels: boolean;
	currentUser: any;
	catalogue: WorkOrderLabel[];
	catalogueFetching: boolean;
	fetchLabels: (search: string) => void;
	createLabel: (entity: { label: string; color: string }) => Promise<WorkOrderLabel>;
	assignLabels: (workOrderId: number | string, ids: number[]) => Promise<any>;
	setLabelsPending: (workOrderId: number | string, pending: boolean) => void;
}

// Labels added/removed in one save, derived by diffing the work order's prior labels
// against the saved set — mirrors the per-label log entries the backend persists.
const diffLabels = (before: WorkOrderLabel[], after: WorkOrderLabel[]) => {
	const beforeIds = new Set(before.map((label) => label.id));
	const afterIds = new Set(after.map((label) => label.id));
	return {
		added: after.filter((label) => !beforeIds.has(label.id)),
		removed: before.filter((label) => !afterIds.has(label.id)),
	};
};

const showLabelsFeedbackToast = (workOrderId: number | string, wasEmpty: boolean) => {
	const feedbackText = wasEmpty
		? `Labels Added to WO #${workOrderId}`
		: `Labels Updated for WO #${workOrderId}`;
	message.success(feedbackText, 3);
};

// Work-order binding for the shared AddLabelModal.
const WorkOrderAddLabelModal: React.FC<WorkOrderProps> = ({
	visible,
	onClose,
	workOrderId,
	existingLabels,
	refreshTable,
	canModifyLabels,
	canApplyLabels,
	currentUser,
	catalogue,
	catalogueFetching,
	fetchLabels,
	createLabel,
	assignLabels,
	setLabelsPending,
}) => {
	// Same broadcast channel the WO notes feed uses, so a label change reaches every
	// open client: the notes feed refetches (showing the label log entry) and the
	// detail/row chip listeners patch their chips — handled purely on the FE.
	const { broadcastLabelChange } = useNotesBroadcast({
		entityIdName: 'workOrderId',
		entityId: workOrderId,
		entityType: WEB_SOCKET_ENTITY_TYPES.WORK_ORDER_NOTES,
		newNoteEntityType: WEB_SOCKET_ENTITY_TYPES.WORK_ORDER,
		currentUser,
	});

	return (
		<AddLabelModal
			visible={visible}
			onClose={onClose}
			entityId={workOrderId}
			existingLabels={existingLabels}
			canModifyLabels={canModifyLabels}
			canApplyLabels={canApplyLabels}
			catalogue={catalogue}
			catalogueFetching={catalogueFetching}
			fetchLabels={fetchLabels}
			createLabel={createLabel}
			assignLabels={assignLabels}
			pickNewLabelColor={pickRandomLabelColor}
			onBeforeAssign={(id) => setLabelsPending(id, true)}
			onAfterAssign={(id, savedLabels, { wasEmpty }) => {
				const { added, removed } = diffLabels(
					existingLabels || [],
					savedLabels as WorkOrderLabel[]
				);
				added.forEach((label) => broadcastLabelChange(label, 'added'));
				removed.forEach((label) => broadcastLabelChange(label, 'removed'));
				showLabelsFeedbackToast(id, wasEmpty);
				if (refreshTable) refreshTable();
			}}
			onAssignSettled={(id) => setLabelsPending(id, false)}
		/>
	);
};

const mapStateToProps = (state) => {
	const dropdownState = nullSafeGetOrElse(DROPDOWN_TARGET_COLLECTION, state.work_order_labels, {});
	const isAllowed = isUserAllowedToAccess(state.session.userType);
	const companyConfig = nullSafeGetOrElse('company_config.detail', state, {});
	const roles = nullSafeGetOrElse('session.currentUser.roles', state, []);
	return {
		userType: state.session.userType,
		currentUser: state.session.currentUser,
		canModifyLabels: isAllowed(PERMISSION_NAMES.MODIFY_WORK_ORDER_LABELS, companyConfig, roles),
		canApplyLabels: isAllowed(PERMISSION_NAMES.APPLY_WORK_ORDER_LABELS, companyConfig, roles),
		catalogue: getRecordsForTargetCollection(state.work_order_labels, DROPDOWN_TARGET_COLLECTION),
		catalogueFetching: nullSafeGetOrElse('fetching', dropdownState, false),
	};
};
const mapDispatchToProps = (dispatch) => ({ dispatch });
const mergeProps = (stateProps, dispatchProps, ownProps: WorkOrderOwnProps) => {
	const { userType, currentUser, canModifyLabels, canApplyLabels, catalogue, catalogueFetching } =
		stateProps;
	const { dispatch } = dispatchProps;
	const thunks = workOrderLabelsRestCrudThunks(userType);
	return {
		...ownProps,
		currentUser,
		canModifyLabels,
		canApplyLabels,
		catalogue,
		catalogueFetching,
		fetchLabels: (search: string) =>
			dispatch(
				thunks.read(
					{ no_pagination: true, ...(search ? { search } : {}) },
					DROPDOWN_TARGET_COLLECTION,
					null,
					LABELS_SORT,
					{},
					false
				)
			),
		// Create into the shared catalogue so the advanced filter sees new labels; the
		// modal itself shows them immediately via `locallyCreated`.
		createLabel: (entity: { label: string; color: string }) =>
			dispatch(thunks.create(entity, 'id', LABELS_TARGET_COLLECTION)),
		assignLabels: (workOrderId: number | string, ids: number[]) =>
			dispatch(updateWorkOrderLabels(userType)(workOrderId, ids)),
		setLabelsPending: (workOrderId: number | string, pending: boolean) =>
			dispatch(setWorkOrderLabelsPendingInStore(workOrderId, pending)),
	};
};

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(WorkOrderAddLabelModal);
